import React from 'react';
import { motion } from 'framer-motion';
import { GlassCard } from '../GlassCard';
import { GlassOptionCard } from '../GlassOptionCard';
import { GlassInput } from '../GlassInput';
import { GlassCTA } from '../GlassCTA';

const ROLE_OPTIONS = [
  'Creative',
  'Marketer',
  'Designer',
  'Student',
  'Founder',
  'Producer',
  'Artist',
  'Developer',
  'Other',
];

interface RoleStepProps {
  name: string;
  roles: string[];
  roleOther: string | null;
  onRolesChange: (roles: string[]) => void;
  onRoleOtherChange: (value: string | null) => void;
  onNext: () => void;
}

export function RoleStep({
  name,
  roles,
  roleOther,
  onRolesChange,
  onRoleOtherChange,
  onNext,
}: RoleStepProps) {
  const toggleRole = (role: string) => {
    if (roles.includes(role)) {
      onRolesChange(roles.filter((r) => r !== role));
      if (role === 'Other') onRoleOtherChange(null);
    } else {
      onRolesChange([...roles, role]);
    }
  };

  const showOtherInput = roles.includes('Other');
  const canContinue = roles.length >= 1;

  return (
    <GlassCard>
      <div className="flex flex-col items-center text-center">
        <h1
          className="font-display text-[28px] font-semibold text-ink tracking-tight"
        >
          Nice to meet you, {name}. What do you do?
        </h1>
        <p
          className="mt-3 text-base"
          style={{ color: 'hsl(var(--ink-2))' }}
        >
          Pick what fits. More than one is fine.
        </p>

        <div className="mt-8 grid w-full grid-cols-3 gap-3 max-[768px]:grid-cols-2">
          {ROLE_OPTIONS.map((role, i) => (
            <motion.div
              key={role}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 + i * 0.04 }}
            >
              <GlassOptionCard
                selected={roles.includes(role)}
                onClick={() => toggleRole(role)}
              >
                <span className="block text-center text-sm font-medium text-ink">
                  {role}
                </span>
              </GlassOptionCard>
            </motion.div>
          ))}
        </div>

        {showOtherInput && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            transition={{ duration: 0.25 }}
            className="mt-4 w-full max-w-md"
          >
            <GlassInput
              autoFocus
              value={roleOther || ''}
              onChange={(e) => onRoleOtherChange(e.target.value)}
              placeholder="What do you do?"
            />
          </motion.div>
        )}

        <div className="mt-10">
          <GlassCTA onClick={onNext} disabled={!canContinue}>
            Confirm
          </GlassCTA>
        </div>
      </div>
    </GlassCard>
  );
}
